import { get, writable } from 'svelte/store';

import {
  createSavedList,
  deleteSavedList,
  fetchSavedList,
  fetchSavedLists,
  removeRecordsFromList,
  updateSavedList,
  type ListScope,
  type SavedList,
  type SavedListDetail
} from '$lib/api/savedlists';
import { env } from '$lib/config/environment';
import { logger } from '$lib';

/**
 * The customer's saved lists, as the app holds them.
 *
 * # Why the index is a store rather than a fetch per screen
 *
 * Three places read it at once: the sidebar draws it, the per-row menu offers
 * it, and the profile's control offers it again. A dropdown that fetched when
 * it opened would spend a request every time somebody glanced at it, and three
 * components fetching independently would disagree the moment one of them
 * created a list.
 *
 * # Every write refreshes the index rather than patching it
 *
 * `entries` is a count the server recomputes on each write, and an
 * organisation-scoped list has colleagues writing to it. Adjusting the number
 * locally would be right only if nothing else had touched it, which is exactly
 * the assumption a shared list breaks. The lists are few and the response is
 * small; re-reading is cheaper than being subtly wrong.
 */

/** Every list the signed-in account may see. */
export const savedLists = writable<SavedList[]>([]);

/** Whether the index is being read. */
export const savedListsLoading = writable(false);

/** The list being looked at, with its records, or null. */
export const openList = writable<SavedListDetail | null>(null);

/** Whether the open list is being read. */
export const openListLoading = writable(false);

/**
 * Read the index.
 *
 * Silent on failure beyond a log: the sidebar showing no lists is a smaller
 * problem than an error banner over an app the customer opened to do something
 * else. A screen that actually needs them says so itself.
 *
 * Does nothing outside the members' build. The public app has no session and
 * the request would be refused before it left — see `api/client`.
 */
export async function loadSavedLists(): Promise<void> {
  if (env.mode !== 'member') return;

  savedListsLoading.set(true);
  try {
    savedLists.set(await fetchSavedLists());
  } catch (error) {
    logger.warn('saved lists: could not read the index', error);
  } finally {
    savedListsLoading.set(false);
  }
}

/**
 * Open a list.
 *
 * Clears what was open before reading the next one. Leaving the previous list
 * on screen under the new list's name is the failure worth avoiding here — the
 * records look plausible and belong to something else.
 */
export async function openSavedList(id: string): Promise<void> {
  openList.set(null);
  openListLoading.set(true);
  try {
    openList.set(await fetchSavedList(id));
  } finally {
    openListLoading.set(false);
  }
}

/** Stop showing a list. */
export function closeSavedList(): void {
  openList.set(null);
}

/** Make a list, and leave the index correct. */
export async function makeSavedList(name: string, scope: ListScope): Promise<SavedList> {
  const created = await createSavedList(name, scope);
  await loadSavedLists();
  return created;
}

/** Rename a list, or change who can see it. */
export async function renameSavedList(
  id: string,
  name: string,
  scope: ListScope
): Promise<SavedList> {
  const updated = await updateSavedList(id, name, scope);
  await loadSavedLists();

  // The open list carries the same fields, and it is what the screen's heading
  // is drawn from. Without this the title still says the old name until
  // something else reloads it.
  openList.update((held) => (held && held.id === id ? { ...held, ...updated } : held));

  return updated;
}

/** Delete a list, closing it if it was the one on screen. */
export async function discardSavedList(id: string): Promise<void> {
  await deleteSavedList(id);
  if (get(openList)?.id === id) closeSavedList();
  await loadSavedLists();
}

/**
 * Take one record out of the list on screen.
 *
 * Optimistic, with the row put back if the server refuses. Removing is the one
 * action here that is undone by simply adding again, and waiting for a round
 * trip to redraw a row the customer has already dismissed makes the list feel
 * like it is arguing.
 */
export async function removeSavedListRecord(recordID: string): Promise<void> {
  const held = get(openList);
  if (!held) return;

  const without = held.records.filter((record) => record.id !== recordID);
  openList.set({ ...held, records: without, entries: without.length });

  try {
    await removeRecordsFromList(held.id, [recordID]);
    // The index carries the count the sidebar draws.
    await loadSavedLists();
  } catch (error) {
    openList.set(held);
    throw error;
  }
}
